import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Zap, CreditCard, ShieldCheck, ArrowRight, Bot, Gauge, Download } from 'lucide-react';

const FREE_FEATURES = [
  { icon: ShieldCheck, text: 'Mechanical Health Check' },
  { icon: Gauge, text: 'Limited Baseline Analysis Report' },
  { icon: Bot, text: '3 AI chat messages per session' },
];

const PAID_FEATURES = [
  { icon: Bot, text: 'Unlimited AI tuning chat' },
  { icon: Zap, text: 'AI-generated tune files (.bin)' },
  { icon: Gauge, text: 'Interactive Map Viewer with heatmaps' },
  { icon: Download, text: 'Tune downloads & version history' },
];

export default function PaywallGate({ feature, user, onUpgrade }) {
  const isUnlimited = user && (user.is_unlimited || user.role === 'admin');
  const hasCredits = user && (user.credits || 0) > 0;

  if (isUnlimited || hasCredits) return null;

  return (
    <Card className="bg-[#0A0A0A] border-[#FF5E1A]/20 rounded-sm overflow-hidden" data-testid="paywall-gate">
      <div className="bg-gradient-to-r from-[#FF5E1A]/10 to-transparent p-1" />
      <CardContent className="py-8 px-6">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-14 h-14 bg-[#FF5E1A]/10 border border-[#FF5E1A]/20 flex items-center justify-center rounded-sm mx-auto mb-5">
            <Lock className="w-6 h-6 text-[#FF5E1A]" />
          </div>

          <h3 className="font-['Outfit'] font-bold text-lg text-white mb-2">
            {feature || 'Full Tuning'} Requires a Package
          </h3>
          <p className="text-sm text-zinc-400 mb-6 max-w-md mx-auto">
            You're on the free tier. Health Check and basic analysis are free — purchase a tuning package to unlock AI-powered tune generation and the full iterative tuning workflow.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-left max-w-md mx-auto">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Free (Current)</p>
              {FREE_FEATURES.map(f => (
                <div key={f.text} className="flex items-center gap-2 py-1.5">
                  <f.icon className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs text-zinc-300">{f.text}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-[10px] text-[#FF5E1A] uppercase tracking-wider font-bold mb-2">Paid Unlock</p>
              {PAID_FEATURES.map(f => (
                <div key={f.text} className="flex items-center gap-2 py-1.5">
                  <f.icon className="w-3.5 h-3.5 text-[#FF5E1A]" />
                  <span className="text-xs text-zinc-300">{f.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={onUpgrade}
              className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm px-8 py-3 font-bold text-sm group"
              data-testid="upgrade-btn">
              <CreditCard className="w-4 h-4 mr-2" />
              Get Started — $99
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <span className="text-xs text-zinc-600">or Standard $175 / Pro $225</span>
          </div>

          <div className="flex items-center justify-center gap-4 mt-4">
            <Badge className="bg-zinc-800 text-zinc-400 text-[9px] px-2 py-0.5 rounded-sm">2 revisions included</Badge>
            <Badge className="bg-zinc-800 text-zinc-400 text-[9px] px-2 py-0.5 rounded-sm">No subscription needed</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
